import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Send } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { issueOneOffSessionInvoices } from "./api";
import type { OneOffSession, OneOffSessionParticipant } from "./types";
import DialogHeader from "./DialogHeader";

export interface IssueOneOffInvoicesTarget {
  session: OneOffSession;
}

interface ParticipantFee {
  participant_id: number;
  handler_name: string;
  email_address: string;
  fee: string;
}

interface IssueOneOffInvoicesDialogProps {
  target: IssueOneOffInvoicesTarget | null;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
  onError: (message: string) => void;
}

const IssueOneOffInvoicesDialog: React.FC<IssueOneOffInvoicesDialogProps> = ({
  target,
  onClose,
  onSuccess,
  onError,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [dueDate, setDueDate] = useState<Dayjs | null>(null);
  const [participants, setParticipants] = useState<ParticipantFee[]>([]);
  const [issuing, setIssuing] = useState(false);

  useEffect(() => {
    if (target) {
      setDueDate(dayjs().add(7, "day"));
      setParticipants(
        target.session.participants
          .filter((p: OneOffSessionParticipant) => p.invoice_id == null)
          .map((p: OneOffSessionParticipant) => ({
            participant_id: p.id,
            handler_name: p.handler_name,
            email_address: p.email_address,
            fee: "0.00",
          })),
      );
    }
  }, [target]);

  const handleUpdateFee = (participantId: number, fee: string) => {
    setParticipants((prev) =>
      prev.map((p) =>
        p.participant_id === participantId ? { ...p, fee } : p,
      ),
    );
  };

  const total = participants.reduce(
    (sum, p) => sum + parseFloat(p.fee || "0"),
    0,
  );

  const canSubmit =
    !issuing &&
    !!target &&
    participants.length > 0 &&
    participants.some((p) => parseFloat(p.fee || "0") !== 0);

  const handleSubmit = async () => {
    if (!target) return;
    try {
      setIssuing(true);
      await issueOneOffSessionInvoices({
        one_off_session_id: target.session.id,
        due_date: dueDate ? dueDate.format("DD/MM/YYYY") : undefined,
        participants: participants.map((p) => ({
          participant_id: p.participant_id,
          fee: parseFloat(p.fee || "0"),
        })),
      });
      await onSuccess();
      onClose();
    } catch (err) {
      console.error("Error issuing one-off session invoices:", err);
      onError("Failed to issue invoices. Please try again.");
    } finally {
      setIssuing(false);
    }
  };

  const sessionDateLabel = target
    ? dayjs(target.session.session_date).format("DD MMM YYYY, HH:mm")
    : "";

  return (
    <Dialog
      open={target != null}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
    >
      <DialogHeader
        title="Issue Invoices"
        subtitle={target?.session.name}
        onClose={onClose}
        closeDisabled={issuing}
      />
      <DialogContent dividers sx={{ px: { xs: 1.5, sm: 3 } }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={{ xs: 2, sm: 3 }}>
            <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Session
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {sessionDateLabel}
              </Typography>
              {target?.session.notes && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {target.session.notes}
                </Typography>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Due Date
              </Typography>
              <DatePicker
                label="Due Date (optional)"
                value={dueDate}
                onChange={setDueDate}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    helperText: "Defaults to 7 days from today if left blank",
                  },
                }}
              />
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Participant Fees
              </Typography>
              {participants.length === 0 ? (
                <Alert severity="info">
                  {target?.session.invoiced_at
                    ? "All participants on this session have already been invoiced. Add someone new to issue more invoices."
                    : "Add participants to the session before issuing invoices."}
                </Alert>
              ) : isMobile ? (
                <Stack spacing={1.25}>
                  {participants.map((participant) => (
                    <Box
                      key={participant.participant_id}
                      sx={{
                        p: 1.25,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: theme.palette.primary.main,
                            fontSize: "0.875rem",
                          }}
                        >
                          {participant.handler_name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {participant.handler_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {participant.email_address}
                          </Typography>
                        </Box>
                      </Box>
                      <TextField
                        label="Fee"
                        value={participant.fee}
                        onChange={(e) =>
                          handleUpdateFee(
                            participant.participant_id,
                            e.target.value,
                          )
                        }
                        type="number"
                        size="small"
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">£</InputAdornment>
                          ),
                        }}
                        fullWidth
                      />
                    </Box>
                  ))}
                </Stack>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Participant</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Fee (£)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {participants.map((participant) => (
                        <TableRow key={participant.participant_id} hover>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  bgcolor: theme.palette.primary.main,
                                  fontSize: "0.875rem",
                                }}
                              >
                                {participant.handler_name.charAt(0)}
                              </Avatar>
                              {participant.handler_name}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {participant.email_address}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              value={participant.fee}
                              onChange={(e) =>
                                handleUpdateFee(
                                  participant.participant_id,
                                  e.target.value,
                                )
                              }
                              type="number"
                              size="small"
                              inputProps={{ min: 0, step: 0.01 }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    £
                                  </InputAdornment>
                                ),
                              }}
                              sx={{ maxWidth: 120 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {participants.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: { xs: 1.25, sm: 2 },
                    bgcolor: theme.palette.grey[100],
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Total: £{total.toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Stack>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions
        sx={{
          p: { xs: 1.5, sm: 2 },
          flexDirection: { xs: "column-reverse", sm: "row" },
          "& > .MuiButton-root": {
            width: { xs: "100%", sm: "auto" },
          },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button onClick={onClose} disabled={issuing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<Send />}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {issuing ? "Issuing..." : "Issue Invoices"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IssueOneOffInvoicesDialog;
